import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Fragment, useState, type ReactNode } from "react";
import { Field, Panel } from "@/lovable-demo/components/bits";
import { ClassChip, SeverityChip } from "@/lovable-demo/components/signals";
import { supabase } from "@/lovable-demo/runtime/client";
import {
  listDispositions,
  listRootCause,
  listSignalsForRecord,
} from "@/lovable-demo/lib/signals/signals";
import { DISPOSITION_LABELS } from "@/lovable-demo/lib/signals/types";
import { fmtCurrency, fmtDate, fmtNumber } from "@/lovable-demo/lib/warranty/calc";
import type { Claim, ProblemRecord } from "@/lovable-demo/lib/warranty/types";

/** M2 enhancements to the existing Problem Record. Read paths only. */

function useRecordSignals(recordId: string) {
  return useQuery({
    queryKey: ["lovable-demo", "record-signals", recordId],
    queryFn: () => listSignalsForRecord(recordId),
  });
}

function useStatement(recordId: string) {
  return useQuery({
    queryKey: ["lovable-demo", "record-statement", recordId],
    queryFn: async () =>
      (
        await supabase
          .from("record_problem_statements")
          .select("*")
          .eq("record_id", recordId)
          .maybeSingle()
      ).data,
  });
}

export function OriginStrip({ record }: { record: ProblemRecord }) {
  const signals = useRecordSignals(record.id).data ?? [];
  const statement = useStatement(record.id).data;
  if (!signals.length && !statement) return null;
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-3 text-xs">
      {statement?.severity ? <SeverityChip severity={statement.severity} /> : null}
      {statement?.severity_rationale && (
        <span className="text-muted-foreground">{statement.severity_rationale}</span>
      )}
      {signals.length > 0 && (
        <span className="ml-auto flex flex-wrap items-center gap-1.5">
          <span className="text-muted-foreground">Originating signals:</span>
          {signals.map((s) => (
            <Link
              key={s.id}
              to="/demo/signal/$id"
              params={{ id: s.id }}
              className="num rounded border border-primary/30 px-1.5 py-0.5 text-primary hover:bg-primary/10"
            >
              {s.code}
            </Link>
          ))}
        </span>
      )}
    </div>
  );
}

export function SignalsOriginChapter({ record }: { record: ProblemRecord }) {
  const signals = useRecordSignals(record.id).data ?? [];
  const ids = signals.map((s) => s.id);
  const disp = useQuery({
    queryKey: ["lovable-demo", "record-disp", record.id, ids.join()],
    queryFn: () => listDispositions(ids),
    enabled: ids.length > 0,
  });
  const rc = useQuery({
    queryKey: ["lovable-demo", "record-rc", record.id, ids.join()],
    queryFn: () => listRootCause(ids),
    enabled: ids.length > 0,
  });
  const audit = useQuery({
    queryKey: ["lovable-demo", "record-creation-audit", record.id],
    queryFn: async () =>
      (
        await supabase
          .from("audit_events")
          .select("*")
          .eq("entity_id", record.id)
          .in("event_type", ["record.created_from_signal", "record.signal_linked"])
          .order("occurred_at", { ascending: false })
      ).data ?? [],
  });

  return (
    <div className="space-y-4">
      <Panel
        title={`Linked signals — ${signals.length}`}
        subtitle="Originating and related signals attached through governed dispositions"
      >
        {signals.length === 0 ? (
          <p className="text-sm text-muted-foreground">No signals are linked to this record yet.</p>
        ) : (
          <ul className="space-y-2">
            {signals.map((s) => (
              <li
                key={s.id}
                className="flex flex-wrap items-center gap-2 rounded border border-border p-2 text-sm"
              >
                <Link
                  to="/demo/signal/$id"
                  params={{ id: s.id }}
                  className="num font-semibold text-primary hover:underline"
                >
                  {s.code}
                </Link>
                <span className="text-foreground">{s.title}</span>
                <ClassChip cls={s.signalClass} />
                <SeverityChip severity={s.currentSeverity} />
              </li>
            ))}
          </ul>
        )}
      </Panel>
      {(rc.data ?? []).length > 0 && (
        <Panel
          title="Verified root cause carried in"
          subtitle="Retained as evidence; re-analysis is not forced"
        >
          {(rc.data ?? []).map((x) => (
            <p key={x.id} className="text-sm text-foreground/90">
              {x.statement}{" "}
              <span className="text-xs text-muted-foreground">
                — {x.source}, {x.verifiedBy}
              </span>
            </p>
          ))}
        </Panel>
      )}
      <Panel
        title="Acceptance / creation decisions"
        subtitle="Authority, rationale and Evidence Snapshot at Decision"
      >
        <ol className="space-y-2">
          {(disp.data ?? []).map((d) => (
            <li key={d.id} className="rounded border border-border p-2 text-sm">
              <p className="font-semibold text-foreground">{DISPOSITION_LABELS[d.disposition]}</p>
              <p className="text-xs text-muted-foreground">{d.rationale}</p>
              <p className="mt-1 text-[0.7rem] text-muted-foreground">
                {fmtDate(d.createdAt)} · {d.decidedByLabel} · authority {d.authorityRuleKey ?? "—"}
                {Object.keys(d.evidenceSnapshot).length ? " · evidence snapshot captured" : ""}
              </p>
            </li>
          ))}
          {(audit.data ?? []).map((a) => (
            <li key={a.id} className="rounded border border-border p-2 text-sm">
              <p className="font-semibold text-foreground">{a.action}</p>
              <p className="text-[0.7rem] text-muted-foreground">
                {fmtDate(a.occurred_at)} · {a.actor_label} · append-only audit entry
              </p>
            </li>
          ))}
          {(disp.data ?? []).length === 0 && (audit.data ?? []).length === 0 && (
            <p className="text-sm text-muted-foreground">
              No governed decisions recorded against this record.
            </p>
          )}
        </ol>
      </Panel>
    </div>
  );
}

export function ProblemStatementChapter({ record }: { record: ProblemRecord }) {
  const st = useStatement(record.id).data;
  const ageDays = Math.max(
    0,
    Math.round((Date.now() - new Date(record.openedAt).getTime()) / 86_400_000),
  );
  return (
    <Panel
      title="Problem statement"
      subtitle="Observed vs expected condition, scope and known boundaries"
    >
      {!st ? (
        <p className="text-sm text-muted-foreground">{record.description}</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Observed condition" value={st.observed_condition} />
          <Field label="Expected condition" value={st.expected_condition} />
          <Field label="Scope" value={st.scope} />
          <Field label="Known boundaries" value={st.known_boundaries} />
          <Field label="Affected product" value={st.affected_product} />
          <Field label="Affected process" value={st.affected_process} />
          <Field label="Affected location" value={st.affected_location} />
          <Field label="Severity" value={<SeverityChip severity={st.severity} />} />
          <Field label="Severity rationale" value={st.severity_rationale} />
          <Field label="Ownership / accountability" value={st.accountable_owner} />
          <Field label="Status / aging" value={`${record.status} · ${ageDays} days open`} />
          <Field
            label="Escalation level"
            value={st.escalation_level ? `Level ${st.escalation_level}` : "None"}
          />
        </div>
      )}
    </Panel>
  );
}

const LOCATION_LABELS: Record<string, string> = {
  plant: "Plant",
  wip: "WIP",
  warehouse: "Warehouse",
  in_transit: "In transit",
  dealer: "Dealer",
  customer: "Customer",
  supplier: "Supplier",
};

export function GovernedContainment({ record }: { record: ProblemRecord }) {
  const q = useQuery({
    queryKey: ["lovable-demo", "record-containment", record.id],
    queryFn: async () => {
      const [c, i] = await Promise.all([
        supabase
          .from("record_containment_actions")
          .select("*")
          .eq("record_id", record.id)
          .order("start_date"),
        supabase.from("record_inventory_controls").select("*").eq("record_id", record.id),
      ]);
      return { actions: c.data ?? [], inventory: i.data ?? [] };
    },
  });
  const actions = q.data?.actions ?? [];
  const inventory = q.data?.inventory ?? [];
  return (
    <div className="mt-4 space-y-4">
      <Panel
        title="Containment actions"
        subtitle="Governed dates, inspection results, quantities, cost and verified effectiveness"
      >
        {actions.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No detailed containment entries for this record.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] text-left text-xs">
              <thead className="text-[0.66rem] uppercase tracking-[0.14em] text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="py-2 pr-3">Action</th>
                  <th className="py-2 pr-3">Start / end</th>
                  <th className="py-2 pr-3">Inspection result</th>
                  <th className="py-2 pr-3">Suspect / contained / rejected</th>
                  <th className="py-2 pr-3">Labour</th>
                  <th className="py-2 pr-3">Parts / scrap</th>
                  <th className="py-2">Effectiveness</th>
                </tr>
              </thead>
              <tbody>
                {actions.map((a) => (
                  <tr key={a.id} className="border-b border-border/50 align-top">
                    <td className="py-2 pr-3 text-foreground">
                      {a.action}
                      <p className="text-muted-foreground">{a.owner}</p>
                    </td>
                    <td className="num py-2 pr-3">
                      {a.start_date ?? "—"} → {a.end_date ?? "open"}
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground">
                      {a.inspection_result}
                      <p className="num">{fmtNumber(a.inspected_quantity)} inspected</p>
                    </td>
                    <td className="num py-2 pr-3">
                      {fmtNumber(a.suspect_quantity)} / {fmtNumber(a.contained_quantity)} /{" "}
                      {fmtNumber(a.rejected_quantity)}
                    </td>
                    <td className="num py-2 pr-3">
                      {fmtNumber(Number(a.labor_hours))} h · {fmtCurrency(Number(a.labor_cost))}
                    </td>
                    <td className="num py-2 pr-3">
                      {fmtCurrency(Number(a.parts_cost))} / {fmtCurrency(Number(a.scrap_cost))}
                    </td>
                    <td className="py-2">
                      {a.effectiveness_verified ? (
                        <span className="text-risk-low">
                          Verified — {a.verified_by}
                          {a.verified_at ? `, ${a.verified_at}` : ""}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Not yet verified</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      <Panel
        title="Inventory controls"
        subtitle="Plant, WIP, warehouse, in-transit, dealer, customer and supplier stock"
      >
        {inventory.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No inventory controls recorded for this record.
          </p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {inventory.map((i) => (
              <div key={i.id} className="rounded border border-border p-3 text-xs">
                <p className="text-[0.66rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  {LOCATION_LABELS[i.location_type] ?? i.location_type}
                </p>
                <p className="mt-1 text-sm text-foreground">{i.location_name}</p>
                <p className="num mt-1">
                  {fmtNumber(i.quantity)} units · <span className="capitalize">{i.status}</span>
                </p>
                <p className="mt-1 text-muted-foreground">
                  {i.owner}
                  {i.notes ? ` — ${i.notes}` : ""}
                </p>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

const normalizeAction = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export function LegacyContainmentHistory({ record }: { record: ProblemRecord }) {
  const q = useQuery({
    queryKey: ["lovable-demo", "record-containment-legacy-match", record.id],
    queryFn: async () =>
      (
        await supabase
          .from("record_containment_actions")
          .select("action")
          .eq("record_id", record.id)
      ).data ?? [],
  });
  const governed = new Set((q.data ?? []).map((row) => normalizeAction(row.action)));
  return (
    <details className="rounded border border-border bg-card print:open">
      <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-foreground">
        Legacy containment history (before governed containment)
      </summary>
      <div className="border-t border-border px-4 py-3">
        <p className="mb-3 text-xs text-muted-foreground">
          Read-only history retained without alteration.
        </p>
        <ul className="space-y-2">
          {record.containment.map((item) => {
            const duplicate = governed.has(normalizeAction(item.action));
            return (
              <li
                key={item.id}
                className="flex flex-wrap items-center gap-3 rounded border border-border/70 bg-elevated/40 p-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{item.action}</p>
                  <p className="num text-xs text-muted-foreground">
                    {item.scope} · {item.owner} · due {fmtDate(item.dueDate)}
                  </p>
                </div>
                {duplicate && (
                  <span className="rounded border border-muted-foreground/40 px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase text-muted-foreground">
                    Superseded by governed row
                  </span>
                )}
                <span className="text-xs capitalize text-muted-foreground">{item.status}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </details>
  );
}

const BASIS_STYLE: Record<string, string> = {
  actual: "border-risk-low/40 text-risk-low",
  forecast: "border-primary/40 text-primary",
  estimated: "border-risk-medium/40 text-risk-medium",
  avoided: "border-border text-muted-foreground",
};
const BASIS_LABEL: Record<string, string> = {
  actual: "Actual",
  forecast: "Forecast",
  estimated: "Estimated",
  avoided: "Avoided (estimated)",
};

export function CostBasis({ record }: { record: ProblemRecord }) {
  const q = useQuery({
    queryKey: ["lovable-demo", "record-costs", record.id],
    queryFn: async () =>
      (await supabase.from("record_cost_lines").select("*").eq("record_id", record.id)).data ?? [],
  });
  const rows = q.data ?? [];
  if (!rows.length) return null;
  const totals = (["actual", "forecast", "estimated", "avoided"] as const).map((b) => ({
    b,
    total: rows.filter((r) => r.basis === b).reduce((a, r) => a + Number(r.amount), 0),
  }));
  return (
    <Panel
      className="mt-4"
      title="Connected cost basis"
      subtitle="Claims, labour, parts, scrap, containment, recovery and delay — actual, forecast and estimated/avoided kept separate"
    >
      <div className="mb-3 grid gap-2 sm:grid-cols-4">
        {totals.map((t) => (
          <div key={t.b} className={`rounded border p-2 ${BASIS_STYLE[t.b]}`}>
            <p className="text-[0.66rem] uppercase tracking-[0.14em]">{BASIS_LABEL[t.b]}</p>
            <p className="num mt-1 text-sm text-foreground">{fmtCurrency(t.total)}</p>
          </div>
        ))}
      </div>
      <table className="w-full text-left text-xs">
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-border/50">
              <td className="py-1.5 pr-3 capitalize text-foreground">{r.category}</td>
              <td className="py-1.5 pr-3">
                <span
                  className={`rounded border px-1.5 py-0.5 text-[0.62rem] uppercase tracking-wider ${BASIS_STYLE[r.basis]}`}
                >
                  {BASIS_LABEL[r.basis]}
                </span>
              </td>
              <td className="num py-1.5 pr-3">{fmtCurrency(Number(r.amount))}</td>
              <td className="py-1.5 text-muted-foreground">{r.source}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

/** Claim row that expands to the richer warranty detail. */
export function ExpandableClaimRow({
  claim,
  colSpan,
  children,
}: {
  claim: Claim;
  colSpan: number;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Fragment>
      <tr
        className="cursor-pointer border-b border-border/50 align-top hover:bg-elevated/40"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        {children}
      </tr>
      {open && (
        <tr className="border-b border-border/50 bg-elevated/30">
          <td colSpan={colSpan} className="px-3 py-3">
            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <Field
                label="Labour"
                value={
                  claim.laborHours != null
                    ? `${claim.laborHours} h × ${fmtCurrency(claim.laborRate ?? 0)} = ${fmtCurrency(claim.laborCost)}`
                    : fmtCurrency(claim.laborCost)
                }
              />
              <Field
                label="Part number / qty"
                value={`${claim.partNumber ?? "—"} × ${claim.partQuantity ?? 1}`}
              />
              <Field label="Parts cost" value={fmtCurrency(claim.partsCost)} />
              <Field
                label="Failure / repair / causal"
                value={`${claim.failureCode} · ${claim.repairCode ?? "—"} · ${claim.causalCode ?? "—"}`}
              />
              <Field label="MIS (months in service)" value={claim.misMonths ?? "—"} />
              <Field label="Repeat repair" value={claim.repeatRepair ? "Yes" : "No"} />
              <Field label="Returned part" value={claim.returnedPartStatus ?? "—"} />
              <Field
                label="Production lot / plant"
                value={`${claim.productionLot ?? "—"} · ${claim.buildPlant ?? "—"}`}
              />
              <Field label="Supplier" value={claim.supplierName ?? "—"} />
              <Field
                label="Signal"
                value={
                  claim.signalId
                    ? claim.signalId.startsWith("sg-w-pr-")
                      ? `SG-W${claim.signalId.slice(8)}`
                      : claim.signalId.toUpperCase()
                    : "—"
                }
              />
            </div>
            {claim.narrative && (
              <p className="mt-3 text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">Narrative: </span>
                {claim.narrative}
              </p>
            )}
            {claim.returnedPartFindings && (
              <p className="mt-1 text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">Returned-part findings: </span>
                {claim.returnedPartFindings}
              </p>
            )}
          </td>
        </tr>
      )}
    </Fragment>
  );
}

export function ClaimTrendPanel({ record }: { record: ProblemRecord }) {
  const q = useQuery({
    queryKey: ["lovable-demo", "claim-trends", record.id],
    queryFn: async () =>
      (
        await supabase
          .from("claim_trends")
          .select("*")
          .eq("record_id", record.id)
          .order("month_index")
      ).data ?? [],
  });
  const rows = q.data ?? [];
  if (!rows.length) return null;
  return (
    <Panel
      className="mt-4"
      title="Claim trend"
      subtitle="Actual, forecast and projected claims shown separately"
    >
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="text-[0.66rem] uppercase tracking-[0.14em] text-muted-foreground">
            <tr className="border-b border-border">
              <th className="py-1.5 pr-3">Month</th>
              {rows.map((r) => (
                <th key={r.id} className="num py-1.5 pr-3">
                  {r.month}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(
              [
                ["Actual", "actual_claims"],
                ["Forecast", "forecast_claims"],
                ["Projected", "ai_predicted_claims"],
              ] as const
            ).map(([label, key]) => (
              <tr key={key} className="border-b border-border/50">
                <td className="py-1.5 pr-3 text-muted-foreground">{label}</td>
                {rows.map((r) => (
                  <td key={r.id} className="num py-1.5 pr-3">
                    {r[key] ?? "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
